// 등록된 당첨번호 목록 컴포넌트 (모바일 최적화 + 페이징)
// src/pages/admin/components/draw/DrawList.tsx

import type { DrawRecord } from '@/pages/admin/components/draw/types';
import Pagination from '@/components/base/Pagination';

interface DrawListProps {
  draws: DrawRecord[];
  loading: boolean;
  onDelete: (id: number) => void;
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export default function DrawList({ draws, loading, onDelete, currentPage, totalCount, pageSize, onPageChange }: DrawListProps) {
  return (
    <div>
      <h4 className="font-heading text-sm text-foreground-700 mb-4">등록된 당첨번호 목록</h4>
      {loading ? (
        <div className="text-center py-10">
          <div className="w-8 h-8 mx-auto mb-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-foreground-400 text-sm">불러오는 중...</p>
        </div>
      ) : draws.length === 0 ? (
        <p className="text-foreground-400 text-sm text-center py-10">등록된 당첨번호가 없습니다.</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-3 px-3 text-foreground-400 font-medium">회차</th>
                  <th className="text-left py-3 px-3 text-foreground-400 font-medium">추첨일</th>
                  <th className="text-left py-3 px-3 text-foreground-400 font-medium">당첨번호</th>
                  <th className="text-left py-3 px-3 text-foreground-400 font-medium">보너스</th>
                  <th className="text-left py-3 px-3 text-foreground-400 font-medium">관리</th>
                </tr>
              </thead>
              <tbody>
                {draws.map((d) => (
                  <tr key={d.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="py-2.5 px-3 text-foreground-800 font-bold">{d.id}회</td>
                    <td className="py-2.5 px-3 text-foreground-500">{d.draw_date}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1">
                        {d.numbers.map((n) => (
                          <span key={n} className="inline-flex w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs items-center justify-center font-bold">
                            {n}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="inline-flex w-6 h-6 rounded-full bg-accent-100 text-accent-700 text-xs items-center justify-center font-bold">
                        {d.bonus}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <button
                        onClick={() => onDelete(d.id)}
                        className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer whitespace-nowrap"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {draws.map((d) => (
              <div key={d.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-foreground-800">{d.id}회</span>
                  <span className="text-xs text-foreground-400">{d.draw_date}</span>
                </div>
                <div className="flex items-center gap-1 mb-3">
                  {d.numbers.map((n) => (
                    <span key={n} className="inline-flex w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-xs items-center justify-center font-bold">
                      {n}
                    </span>
                  ))}
                  <span className="text-foreground-300 text-xs mx-0.5">+</span>
                  <span className="inline-flex w-7 h-7 rounded-full bg-accent-100 text-accent-700 text-xs items-center justify-center font-bold">
                    {d.bonus}
                  </span>
                </div>
                <button
                  onClick={() => onDelete(d.id)}
                  className="text-xs px-3 py-1.5 rounded-lg text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      <Pagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </div>
  );
}